<template>
  <div class="jigsaw module-hover-effect" :style="wrapperStyle">
    <!-- Masked gradient shape -->
    <div class="jigsaw-mask" :style="maskStyle"></div>

    <!-- Content area (centered) -->
    <div class="jigsaw-module-content" :style="contentStyle">
      <slot>
        <div class="relative flex items-center justify-center w-full h-full">
          <div class="rounded-full flex items-center justify-center shadow" :style="circleStyle">
            <i :class="icon" :style="{ color: iconColor, fontSize: iconSize }" />
          </div>
          <div v-if="showBadge" class="absolute -top-1 -right-1 rounded-full" :style="{ width: '10px', height: '10px', background: badgeColor }"></div>
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  size: { type: [Number, String], default: 128 },
  gradient: { type: String, default: 'linear-gradient(135deg, #0891b2 0%, #3b82f6 50%, #0891b2 100%)' },
  maskUrl: { type: String, default: 'https://vue-project.sgp1.digitaloceanspaces.com/2025/08/1756488804242.png' },
  contentScale: { type: Number, default: 0.62 },
  icon: { type: String, default: 'fas fa-comments' },
  iconColor: { type: String, default: '#0ea5e9' },
  iconSize: { type: String, default: '1.25rem' },
  circleBg: { type: String, default: '#ffffff' },
  circleSize: { type: Number, default: 56 },
  showBadge: { type: Boolean, default: false },
  badgeColor: { type: String, default: '#22c55e' }
})

const pxVal = (v) => (typeof v === 'number' ? `${v}px` : v)

const wrapperStyle = computed(() => ({
  width: pxVal(props.size),
  height: pxVal(props.size),
  position: 'relative'
}))

const maskStyle = computed(() => {
  const url = `url('${props.maskUrl}')`
  return {
    position: 'absolute',
    inset: '0',
    background: props.gradient,
    maskImage: url,
    maskSize: 'contain',
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    WebkitMaskImage: url,
    WebkitMaskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    pointerEvents: 'none'
  }
})

const contentStyle = computed(() => {
  const sizeNum = typeof props.size === 'number' ? props.size : parseInt(props.size, 10) || 0
  const dim = `${Math.round(sizeNum * props.contentScale)}px`
  return {
    position: 'absolute',
    top: '53%',
    left: '43%',
    transform: 'translate(-50%, -50%)',
    width: dim,
    height: dim,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10
  }
})

const circleStyle = computed(() => ({
  width: pxVal(props.circleSize),
  height: pxVal(props.circleSize),
  background: props.circleBg
}))
</script>

<style scoped>
.jigsaw {
  filter: drop-shadow(0 8px 20px rgba(0, 0, 0, 0.3));
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.module-hover-effect:hover {
  transform: scale(1.08) rotate(0deg) !important;
  z-index: 50;
  filter: drop-shadow(0 20px 40px rgba(0, 0, 0, 0.5));
}

/* Optional subtle inner glow on hover */
.jigsaw::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at center, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
}
.jigsaw:hover::before { opacity: 1; }
</style>
